import React, { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Instagram, Youtube, Plus, Minus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQS = [
  {
    q: "How long do the bottles keep?",
    a: "Cold-stored, our bottles are best enjoyed within 21 days of opening. Unopened, refer to the small mark on the neck of every bottle.",
  },
  {
    q: "How do you ship?",
    a: "We cold-pack every order in insulated liners with a small pouch of recycled paper wool. Shipping is via overnight or two-day services only.",
  },
  {
    q: "Do you offer gift cards?",
    a: "Yes — digital gift cards can be requested by email while our on-site gifting experience is being crafted.",
  },
  {
    q: "Where else can I find you?",
    a: "In select farmers markets across California, New York, Washington DC and Seattle. See our Markets page for the current schedule.",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [openIdx, setOpenIdx] = useState(0);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message received. Thank you.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-[color:var(--sr-bg)] pt-28 md:pt-32">
      <section className="sr-container">
        <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
          <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> Contact
        </p>
        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="font-serif text-6xl md:text-8xl lg:text-[9rem] leading-[0.9] tracking-tight text-[color:var(--sr-primary)] max-w-4xl"
        >
          Say hello. <br />
          <em className="italic font-light text-[color:var(--sr-accent)]">
            We read every note.
          </em>
        </motion.h1>
      </section>

      <section className="sr-container mt-20 md:mt-28 grid grid-cols-12 gap-6 md:gap-12">
        <div className="col-span-12 lg:col-span-5 space-y-10">
          <div>
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-3">
              Elsewhere
            </p>
            <ul className="space-y-3">
              <li>
                <a
                  href="#"
                  className="font-serif text-2xl text-[color:var(--sr-primary)] hover:text-[color:var(--sr-accent)] transition-colors inline-flex items-center gap-3"
                >
                  <Instagram size={18} strokeWidth={1.25} /> @thesmilingroots
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="font-serif text-2xl text-[color:var(--sr-primary)] hover:text-[color:var(--sr-accent)] transition-colors inline-flex items-center gap-3"
                >
                  <Youtube size={18} strokeWidth={1.25} /> Long-form journals
                </a>
              </li>
            </ul>
          </div>
          <div>
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-3">Direct</p>
            <a
              href="mailto:smilingroots@gmail.com"
              className="font-serif text-2xl text-[color:var(--sr-primary)] hover:text-[color:var(--sr-accent)] transition-colors"
            >
              smilingroots@gmail.com
            </a>
          </div>
        </div>

        <div className="col-span-12 lg:col-span-7 lg:pl-8">
          {sent ? (
            <div className="border-t hairline pt-8" data-testid="contact-thanks">
              <p className="font-serif text-4xl text-[color:var(--sr-primary)] leading-tight">
                Message received.
              </p>
              <p className="mt-3 text-[color:var(--sr-muted)] max-w-md">
                Thank you for writing. We reply personally, usually within a
                few days.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5 border-t hairline pt-8" data-testid="contact-form">
              <div className="grid grid-cols-2 gap-5">
                <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="ct-name" required />
                <Field type="email" label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="ct-email" required />
              </div>
              <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testid="ct-subject" />
              <Field textarea label="Your message" value={form.message} onChange={(v) => setForm({ ...form, message: v })} testid="ct-message" required />
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-2 group inline-flex items-center justify-between w-full px-8 py-4 bg-[color:var(--sr-primary)] text-[color:var(--sr-secondary)] label-eyebrow hover:bg-[color:var(--sr-primary-deep)] transition-colors disabled:opacity-40"
              >
                <span>{submitting ? "Sending" : "Send message"}</span>
                <ArrowRight size={16} strokeWidth={1.25} />
              </button>
            </form>
          )}
        </div>
      </section>

      <section className="sr-container py-24 md:py-40" data-testid="faq">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-4">
            <p className="label-eyebrow text-[color:var(--sr-primary)] mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--sr-accent)]" /> FAQ
            </p>
            <h2 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tight text-[color:var(--sr-primary)]">
              Small things worth knowing.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-8">
            <ul className="border-t hairline">
              {FAQS.map((f, i) => (
                <li key={i} className="border-b hairline" data-testid={`faq-${i}`}>
                  <button
                    onClick={() => setOpenIdx(openIdx === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 py-6 text-left text-[color:var(--sr-primary)]"
                    data-testid={`faq-toggle-${i}`}
                  >
                    <span className="font-serif text-2xl md:text-3xl leading-tight">
                      {f.q}
                    </span>
                    <span className="p-1 shrink-0 text-[color:var(--sr-primary)]/70">
                      {openIdx === i ? <Minus size={18} strokeWidth={1.25} /> : <Plus size={18} strokeWidth={1.25} />}
                    </span>
                  </button>
                  <AnimatePresence initial={false}>
                    {openIdx === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <p className="pb-6 pr-10 text-[color:var(--sr-text)]/75 leading-relaxed max-w-2xl">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, textarea }) {
  const props = {
    value,
    onChange: (e) => onChange(e.target.value),
    "data-testid": testid,
    required,
    className:
      "w-full bg-transparent border-b hairline py-3 text-[color:var(--sr-primary)] placeholder:text-[color:var(--sr-muted)]/50 outline-none focus:border-[color:var(--sr-primary)]",
  };
  return (
    <label className="block">
      <span className="label-eyebrow text-[color:var(--sr-muted)]">{label}</span>
      {textarea ? <textarea rows={5} {...props} /> : <input type={type} {...props} />}
    </label>
  );
}
